// src/pages/agents/ContribuablesList.jsx
import React, { useState, useMemo, useEffect, useCallback } from "react";
import Header from "../../layouts/Header";
import Footer from "../../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import Select from 'react-select';
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Pagination, 
  Dropdown, 
  Badge, 
  Modal, 
  Alert, 
  Spinner, 
  Toast, 
  ToastContainer,
  Tabs,
  Tab
} from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import axios from "axios";
import {
  Search,
  PlusCircle,
  Eye,
  FileText,
  Building,
  Person,
  Download,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  Envelope,
  Phone,
  ChevronDown,
  ChevronUp,
  PersonCheck,
  PersonX,
  ExclamationCircle,
  Check,
  X,
  ArrowRepeat,
  Calendar3,
  Tag,
  Briefcase,
  People,
  Archive,
  Lock,
  Shield,
  FileExcel,
  FilePdf,
  GeoAlt,
  Buildings
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../../constants/api_constants";
import { printAvisPassage, enregistrerImpressionsAvis, describeImpressionsResult } from "./avisPassagePrint";

const TAXPAYERS_ENDPOINT = `${API_BASE_URL}/taxpayers`;
const LOCALITES_ENDPOINT = `${API_BASE_URL}/localites`;

const types = [
  { value: "all", label: "Tous les types" },
  { value: "particulier", label: "Particulier" },
  { value: "entreprise", label: "Entreprise" },
  { value: "commercant", label: "Commerçant" },
  { value: "professionnel", label: "Profession libérale" }
];

const statuses = [
  { value: "all", label: "Tous les statuts" },
  { value: "actif", label: "Actif" },
  { value: "inactif", label: "Inactif" },
  { value: "en_retard", label: "En retard" }
];

// 👈 NOUVEAU: Options de filtre par marché
const marketFilters = [
  { value: "all", label: "Tous" },
  { value: "with_market", label: "Avec marché" },
  { value: "without_market", label: "Hors marché" }
];

export default function ContribuablesList() {
  const navigate = useNavigate();
  const defaultCurrency = sessionStorage.getItem("default_currency") || 'XOF';

  // États principaux
  const [activeTab, setActiveTab] = useState('active');
  const [contribuables, setContribuables] = useState([]);
  const [trashedContribuables, setTrashedContribuables] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // États pour la pagination API
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    from: 0,
    to: 0
  });
  
  // États pour les modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showBulkRestoreModal, setShowBulkRestoreModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // États pour les données
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [contactToRestore, setContactToRestore] = useState(null);
  
  // États de chargement
  const [updatingContact, setUpdatingContact] = useState(false);
  const [deletingContact, setDeletingContact] = useState(false);
  const [deletingContacts, setDeletingContacts] = useState(false);
  const [restoringContact, setRestoringContact] = useState(false);
  const [restoringContacts, setRestoringContacts] = useState(false);
  
  // États pour les formulaires
  const [editContact, setEditContact] = useState({
    contribuable_id: '',
    nom_contribuable: '',
    email: '',
    telephone: '',
    adresse: '',
    activite_contribuable: '',
    full_name: '',
    first_name: '',
    telephone_contr: ''
  });

  // États pour les filtres
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedMarketFilter, setSelectedMarketFilter] = useState('all'); // 👈 NOUVEAU
  
  // États pour les statistiques des marchés (GLOBALES - sur tous les contribuables)
  const [marcheStats, setMarcheStats] = useState({
    withMarket: 0,
    withoutMarket: 0,
    total: 0,
    loading: false
  });
  
  // États pour les notifications
  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });
  
  // États pour les catégories
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // États pour l'export par localité
  const [localites, setLocalites] = useState([]);
  const [localitesMap, setLocalitesMap] = useState({});
  const [loadingLocalites, setLoadingLocalites] = useState(false);
  const [selectedQuartier, setSelectedQuartier] = useState('');
  const [selectedCommune, setSelectedCommune] = useState('');
  const [communes, setCommunes] = useState([]);
  const [quartiers, setQuartiers] = useState([]);
  const [allContribuablesForExport, setAllContribuablesForExport] = useState([]);
  const [localitesLoaded, setLocalitesLoaded] = useState(false);
  
  // Timeout pour debounce
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Récupérer les infos de l'utilisateur connecté
  const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
  const userRoleId = userData?.role_id || null;
  const userMarcheId = userData?.id_marche || null;
  const hasMarket = !!userMarcheId;

  // Déterminer les rôles
  const isAdminPrincipal = userRoleId === 1003;
  const isSuperAdmin = userRoleId === 1001;
  const isAdminMarche = userRoleId === 1005;
  const isSuperviseur = userRoleId === 1007;
  const isAgent = userRoleId === 1012;
  const isCollecteur = userRoleId === 1013;

  const getAuthToken = () => sessionStorage.getItem("authToken");

  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // ✅ 1. showAlert et showNotification
  const showNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [{ id, message, type }, ...prev]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 5000);
  };

  const showAlert = (message, type = 'warning') => {
    setAlertMessage({ show: true, message, type });
    setTimeout(() => setAlertMessage({ show: false, message: '', type: 'info' }), 5000);
  };

  const hideAlert = () => setAlertMessage({ show: false, message: '', type: 'info' });

  // ✅ 2. formatApiData avec les champs marché
  const formatApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];
    return apiData.map(item => {
      let type = "entreprise";
      if (item.activite_contribuable) {
        const activiteStr = item.activite_contribuable.toString();
        if (activiteStr.startsWith("1")) type = "particulier";
        if (activiteStr.startsWith("2")) type = "commercant";
        if (activiteStr.startsWith("3")) type = "professionnel";
      }
      
      let displayStatus = "actif";
      if (item.is_active === -1) displayStatus = "supprimé";
      else if (item.is_active === 0) displayStatus = "inactif";
      else displayStatus = "actif";
      
      // ✅ Détection du marché
      const idMarche = item.id_marche;
      const hasMarket = idMarche !== null && idMarche !== undefined && idMarche !== '' && idMarche !== 'null' && idMarche !== 'undefined';
      const marcheNom = hasMarket ? (item.marche_nom || item.nom_marche || 'Marché') : null;
      
      return {
        id: item.id,
        matricule: `TXC${item.contribuable_id?.toString().slice(-6) || item.id?.toString().slice(-6)}`,
        contribuable_id: item.contribuable_id,
        nom: item.nom_contribuable,
        type: type,
        email: item.email || item.admin_email || "",
        telephone: item.telephone || "",
        adresse: item.adresse || "Non spécifiée",
        localite_id: item.localite_id || null,
        is_active: item.is_active,
        statut: displayStatus,
        created_at: item.created_at || "",
        dateInscription: item.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
        admin_full_name: item.admin_full_name || "",
        first_name: item.first_name || "",
        hasMarket: hasMarket,
        marcheNom: marcheNom,
        marcheId: idMarche || null,
        apiData: item
      };
    });
  };

  // ✅ 3. Fonction pour charger les statistiques globales des marchés
  const loadGlobalMarcheStats = useCallback(async () => {
    setMarcheStats(prev => ({ ...prev, loading: true }));
    try {
      const params = new URLSearchParams();
      params.append('all', 'true');
      
      const response = await axios.get(
        `${TAXPAYERS_ENDPOINT}/liste?${params.toString()}`, 
        { headers: getHeaders() }
      );
      
      if (response.data && response.data.success) {
        const data = response.data.data || [];
        const formattedData = formatApiData(data);
        
        const withMarket = formattedData.filter(item => item.hasMarket === true).length;
        const withoutMarket = formattedData.filter(item => item.hasMarket === false).length;
        
        setMarcheStats({
          withMarket: withMarket,
          withoutMarket: withoutMarket,
          total: formattedData.length,
          loading: false
        });
      } else {
        setMarcheStats(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('❌ Erreur chargement stats marchés:', error);
      setMarcheStats(prev => ({ ...prev, loading: false }));
    }
  }, []);

  // ✅ 4. Fonction pour le badge de marché
  const getMarcheBadge = (hasMarket, marcheNom) => {
    if (hasMarket && marcheNom) {
      return (
        <Badge bg="primary" className="d-flex align-items-center px-2 py-1" style={{ fontSize: '10px', borderRadius: '20px', maxWidth: '150px' }}>
          <Buildings size={10} className="me-1" />
          <span className="text-truncate" style={{ maxWidth: '100px' }} title={marcheNom}>
            {marcheNom}
          </span>
        </Badge>
      );
    }
    return (
      <Badge bg="warning" className="d-flex align-items-center px-2 py-1" style={{ fontSize: '10px', borderRadius: '20px' }}>
        <XCircle size={10} className="me-1" />
        Hors marché
      </Badge>
    );
  };

  // ✅ 5. Fonction pour charger les localités
  const fetchLocalites = useCallback(async () => {
    if (localitesLoaded) return localites;
    
    try {
      setLoadingLocalites(true);
      const response = await axios.get(LOCALITES_ENDPOINT, { headers: getHeaders() });
      
      let allLocalites = [];
      if (response.data && Array.isArray(response.data)) allLocalites = response.data;
      else if (response.data && response.data.data && Array.isArray(response.data.data)) allLocalites = response.data.data;
      
      console.log(`📊 ${allLocalites.length} localités chargées`);
      
      setLocalites(allLocalites);
      
      const map = {};
      allLocalites.forEach(l => {
        map[String(l.localite_id)] = l;
      });
      setLocalitesMap(map);
      
      const communesList = allLocalites.filter(l => l.type_code === 1000);
      setCommunes(communesList);
      console.log(`🏙️ ${communesList.length} communes trouvées`);
      
      const quartiersList = allLocalites.filter(l => l.type_code === 1002);
      setQuartiers(quartiersList);
      console.log(`🏘️ ${quartiersList.length} quartiers trouvés`);
      
      setLocalitesLoaded(true);
      return allLocalites;
    } catch (error) {
      console.error("❌ Erreur chargement localités:", error);
      showAlert("Erreur lors du chargement des localités", 'danger');
      return [];
    } finally {
      setLoadingLocalites(false);
    }
  }, [localitesLoaded, getHeaders]);

  // ✅ 6. Fonctions getLocalite
  const getLocaliteById = (localiteId) => {
    if (!localiteId) return null;
    const idStr = String(localiteId);
    return localitesMap[idStr] || null;
  };

  const getLocaliteName = (localiteId) => {
    const loc = getLocaliteById(localiteId);
    return loc ? loc.nom : 'Non spécifié';
  };

  // ✅ 7. Fonction pour récupérer les données de taxe
  const fetchTaxDataForExport = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/rapports/complet`, { 
        headers: getHeaders(),
        params: {
          start_date: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
          end_date: new Date().toISOString().split('T')[0],
          contribuables_page: 1,
          contribuables_per_page: 10000
        }
      });
      
      if (response.data.success && response.data.contribuables?.data) {
        console.log(`✅ ${response.data.contribuables.data.length} contribuables avec données de taxe récupérés`);
        return response.data.contribuables.data;
      }
      return [];
    } catch (error) {
      console.error("❌ Erreur récupération données de taxe:", error);
      return [];
    }
  };

  const getParentChain = (localiteId, chain = []) => {
    if (!localiteId) return chain;
    const current = getLocaliteById(localiteId);
    if (!current) return chain;
    chain.unshift(current);
    if (current.parent_id) {
      return getParentChain(current.parent_id, chain);
    }
    return chain;
  };

  const getCommuneFromLocalite = (localiteId) => {
    if (!localiteId) return null;
    const chain = getParentChain(localiteId);
    const commune = chain.find(l => l.type_code === 1000);
    return commune || null;
  };

  const getQuartierFromLocalite = (localiteId) => {
    if (!localiteId) return null;
    const chain = getParentChain(localiteId);
    const quartier = chain.find(l => l.type_code === 1002);
    return quartier || null;
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + defaultCurrency;
  };

  // ✅ 8. Fonction exportContribuablesByLocalite
  const exportContribuablesByLocalite = async () => {
    setExportLoading(true);
    try {
      let exportData = await fetchAllContribuablesForExport();
      
      if (!exportData || exportData.length === 0) {
        showAlert("Aucun contribuable à exporter", 'warning');
        setExportLoading(false);
        return;
      }
      
      console.log(`📊 ${exportData.length} contribuables récupérés`);
      
      const taxData = await fetchTaxDataForExport();
      console.log(`📊 ${taxData.length} contribuables avec données de taxe récupérés`);
      
      const taxMap = {};
      taxData.forEach(item => {
        if (item.contribuable_id) {
          taxMap[String(item.contribuable_id)] = {
            totalTaxe: parseFloat(item.montantInitialTotal || item.montant_total || 0),
            totalTaxeCollecte: parseFloat(item.montantPayeTotal || item.montant_paye || 0),
            totalTaxeNonCollecte: parseFloat(item.montantRestant || item.totalRestant || 0),
            tauxPaiement: parseFloat(item.tauxPaiement || 0),
            nombreSouscriptions: item.nombreSouscriptions || 0
          };
        }
      });
      
      console.log(`🔍 Map des taxes créé avec ${Object.keys(taxMap).length} entrées`);
      
      if (selectedCommune) {
        const communeId = String(selectedCommune);
        const quartiersInCommune = quartiers.filter(q => String(q.parent_id) === communeId);
        const quartierIds = quartiersInCommune.map(q => String(q.localite_id));
        const allLocaliteIds = [communeId, ...quartierIds];
        
        console.log(`🏙️ Filtrage par commune ${communeId}: ${allLocaliteIds.length} localités concernées`);
        
        const beforeFilter = exportData.length;
        exportData = exportData.filter(item => {
          const localiteId = item.localite_id ? String(item.localite_id) : null;
          return localiteId && allLocaliteIds.includes(localiteId);
        });
        console.log(`  Avant: ${beforeFilter} → Après: ${exportData.length} contribuables`);
      }
      
      if (selectedQuartier) {
        const quartierId = String(selectedQuartier);
        const beforeFilter = exportData.length;
        exportData = exportData.filter(item => {
          const localiteId = item.localite_id ? String(item.localite_id) : null;
          return localiteId === quartierId;
        });
        console.log(`🏘️ Filtrage par quartier ${quartierId}: ${beforeFilter} → ${exportData.length} contribuables`);
      }
      
      if (exportData.length === 0) {
        showAlert("Aucun contribuable trouvé dans la localité sélectionnée", 'warning');
        setExportLoading(false);
        return;
      }
      
      let totalTaxeGlobal = 0;
      let totalTaxeCollecteGlobal = 0;
      let totalTaxeNonCollecteGlobal = 0;
      let totalContribuablesAvecTaxe = 0;
      
      const excelRows = exportData.map((item, index) => {
        const localiteId = item.localite_id ? String(item.localite_id) : null;
        const quartier = localiteId ? getQuartierFromLocalite(localiteId) : null;
        const commune = localiteId ? getCommuneFromLocalite(localiteId) : null;
        
        let type = "Entreprise";
        if (item.activite_contribuable) {
          const activiteStr = item.activite_contribuable.toString();
          if (activiteStr.startsWith("1")) type = "Particulier";
          else if (activiteStr.startsWith("2")) type = "Commerçant";
          else if (activiteStr.startsWith("3")) type = "Professionnel";
        }
        
        let status = "Actif";
        if (item.is_active === -1) status = "Supprimé";
        else if (item.is_active === 0) status = "Inactif";
        
        const taxInfo = taxMap[String(item.contribuable_id)] || {};
        const totalTaxe = taxInfo.totalTaxe || 0;
        const totalTaxeCollecte = taxInfo.totalTaxeCollecte || 0;
        const totalTaxeNonCollecte = taxInfo.totalTaxeNonCollecte || 0;
        const tauxPaiement = taxInfo.tauxPaiement || 0;
        const nombreSouscriptions = taxInfo.nombreSouscriptions || 0;
        
        totalTaxeGlobal += totalTaxe;
        totalTaxeCollecteGlobal += totalTaxeCollecte;
        totalTaxeNonCollecteGlobal += totalTaxeNonCollecte;
        if (totalTaxe > 0) totalContribuablesAvecTaxe++;
        
        return {
          'N°': index + 1,
          'ID Contribuable': item.contribuable_id || '',
          'Nom': item.nom_contribuable || '',
          'Téléphone': item.telephone || '',
          'Email': item.email || item.admin_email || '',
          'Adresse': item.adresse || '',
          'Quartier': quartier ? quartier.nom : 'Non spécifié',
          'Commune': commune ? commune.nom : 'Non spécifié',
          'Type': type,
          'Statut': status,
          "Date d'inscription": item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : '',
          'Total Taxe (Prévu)': formatCurrency(totalTaxe),
          'Total Taxe Collecté (Payé)': formatCurrency(totalTaxeCollecte),
          'Taxe Non Collecté (Reste)': formatCurrency(totalTaxeNonCollecte),
          'Taux Recouvrement (%)': totalTaxe > 0 ? ((totalTaxeCollecte / totalTaxe) * 100).toFixed(2) : '0.00'
        };
      });
      
      const tauxRecouvrementGlobal = totalTaxeGlobal > 0 ? (totalTaxeCollecteGlobal / totalTaxeGlobal) * 100 : 0;
      
      excelRows.push({
        'N°': '',
        'ID Contribuable': '',
        'Nom': '=== SYNTHÈSE COMPTABLE ===',
        'Téléphone': '',
        'Email': '',
        'Adresse': '',
        'Quartier': '',
        'Commune': '',
        'Type': '',
        'Statut': '',
        "Date d'inscription": '',
        'Total Taxe (Prévu)': formatCurrency(totalTaxeGlobal),
        'Total Taxe Collecté (Payé)': formatCurrency(totalTaxeCollecteGlobal),
        'Taxe Non Collecté (Reste)': formatCurrency(totalTaxeNonCollecteGlobal),
        'Taux Recouvrement (%)': tauxRecouvrementGlobal.toFixed(2)
      });
      
      excelRows.push({
        'N°': '',
        'ID Contribuable': '',
        'Nom': '=== RÉSUMÉ ===',
        'Téléphone': `Total contribuables: ${exportData.length}`,
        'Email': `Avec taxe: ${totalContribuablesAvecTaxe}`,
        'Adresse': `Sans taxe: ${exportData.length - totalContribuablesAvecTaxe}`,
        'Quartier': '',
        'Commune': '',
        'Type': '',
        'Statut': '',
        "Date d'inscription": '',
        'Total Taxe (Prévu)': '',
        'Total Taxe Collecté (Payé)': '',
        'Taxe Non Collecté (Reste)': '',
        'Taux Recouvrement (%)': ''
      });
      
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelRows);
      
      const colWidths = [
        { wch: 6 }, { wch: 16 }, { wch: 30 }, { wch: 16 }, { wch: 25 },
        { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 12 },
        { wch: 18 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 18 }
      ];
      ws['!cols'] = colWidths;
      XLSX.utils.book_append_sheet(wb, ws, "Contribuables");
      
      const syntheseData = [
        { 'Indicateur': 'Nombre total de contribuables', 'Valeur': exportData.length },
        { 'Indicateur': 'Contribuables avec taxe', 'Valeur': totalContribuablesAvecTaxe },
        { 'Indicateur': 'Contribuables sans taxe', 'Valeur': exportData.length - totalContribuablesAvecTaxe },
        { 'Indicateur': '', 'Valeur': '' },
        { 'Indicateur': 'TOTAL TAXE PRÉVISIONNELLE', 'Valeur': formatCurrency(totalTaxeGlobal) },
        { 'Indicateur': 'TOTAL TAXE COLLECTÉE', 'Valeur': formatCurrency(totalTaxeCollecteGlobal) },
        { 'Indicateur': 'TAXE NON COLLECTÉE (RESTE)', 'Valeur': formatCurrency(totalTaxeNonCollecteGlobal) },
        { 'Indicateur': 'TAUX DE RECOUVREMENT GLOBAL', 'Valeur': tauxRecouvrementGlobal.toFixed(2) + '%' },
        { 'Indicateur': '', 'Valeur': '' },
        { 'Indicateur': 'Date de génération', 'Valeur': new Date().toLocaleString('fr-FR') }
      ];
      const syntheseSheet = XLSX.utils.json_to_sheet(syntheseData);
      syntheseSheet['!cols'] = [{ wch: 35 }, { wch: 25 }];
      XLSX.utils.book_append_sheet(wb, syntheseSheet, "Synthèse Comptable");
      
      let fileName = 'contribuables';
      if (selectedQuartier) {
        const quartierName = getLocaliteName(String(selectedQuartier));
        fileName = `contribuables_quartier_${quartierName}`;
      } else if (selectedCommune) {
        const communeName = getLocaliteName(String(selectedCommune));
        fileName = `contribuables_commune_${communeName}`;
      } else {
        fileName = 'contribuables_tous';
      }
      fileName += `_${new Date().toISOString().split('T')[0]}`;
      
      XLSX.writeFile(wb, `${fileName}.xlsx`);
      
      showNotification(`✅ Export Excel terminé (${exportData.length} contribuables)`, 'success');
      setShowExportModal(false);
      setSelectedCommune('');
      setSelectedQuartier('');
      
    } catch (error) {
      console.error("❌ Erreur export:", error);
      showAlert(`Erreur: ${error.message || 'Erreur inconnue'}`, 'danger');
    } finally {
      setExportLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`${API_BASE_URL}/usager-categories`, { headers: getHeaders() });
      if (response.data && Array.isArray(response.data)) setCategories(response.data);
    } catch (error) {
      showAlert(`Erreur: ${error.message}`, 'danger');
    } finally {
      setLoadingCategories(false);
    }
  };

  // ✅ 9. Fonction fetchAllContribuablesForExport
  const fetchAllContribuablesForExport = async () => {
    try {
      setExportLoading(true);
      
      const params = new URLSearchParams();
      params.append('all', 'true');
      
      const response = await axios.get(
        `${TAXPAYERS_ENDPOINT}/liste?${params.toString()}`, 
        { headers: getHeaders() }
      );
      
      if (response.data && response.data.success) {
        const data = response.data.data || [];
        console.log(`✅ ${data.length} contribuables récupérés pour l'export`);
        setAllContribuablesForExport(data);
        return data;
      }
      return [];
      
    } catch (error) {
      console.error("❌ Erreur récupération contribuables:", error);
      showAlert(`Erreur: ${error.message}`, 'danger');
      return [];
    } finally {
      setExportLoading(false);
    }
  };

  // ✅ 10. Fonction fetchAllContribuablesForDisplay
  const fetchAllContribuablesForDisplay = async () => {
    try {
      let allData = [];
      let currentPageNum = 1;
      const perPage = 1000;
      let hasMore = true;
      
      console.log('📊 Début de la récupération de tous les contribuables pour affichage...');
      
      while (hasMore) {
        const params = new URLSearchParams();
        params.append('page', currentPageNum);
        params.append('per_page', perPage);
        if (filterText) params.append('search', filterText);
        if (selectedType !== 'all') params.append('type', selectedType);
        if (selectedStatus !== 'all') params.append('status', selectedStatus);
        if (selectedMarketFilter === 'with_market') {
          params.append('has_market', 'true');
        } else if (selectedMarketFilter === 'without_market') {
          params.append('has_market', 'false');
        }
        params.append('sort_field', sortField);
        params.append('sort_direction', sortDirection);
        
        const response = await axios.get(
          `${TAXPAYERS_ENDPOINT}/liste?${params.toString()}`, 
          { headers: getHeaders() }
        );
        
        if (response.data && response.data.success) {
          const pageData = response.data.data || [];
          allData = [...allData, ...pageData];
          
          const paginationData = response.data.pagination;
          if (paginationData && currentPageNum < paginationData.last_page) {
            currentPageNum++;
          } else {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }
      }
      
      console.log(`✅ Récupération terminée : ${allData.length} contribuables pour affichage`);
      return allData;
      
    } catch (error) {
      console.error("❌ Erreur récupération contribuables:", error);
      throw error;
    }
  };

  // ✅ 11. Fonction fetchContribuables
  const fetchContribuables = useCallback(async (page = 1, perPage = itemsPerPage, resetSelection = true) => {
    try {
      setLoading(true);
      setError(null);
      setAccessDenied(false);
      
      if (perPage === -1) {
        const allData = await fetchAllContribuablesForDisplay();
        const formattedData = formatApiData(allData);
        
        // 👈 Appliquer le filtre marché sur les données formatées (fallback client-side)
        let filteredData = formattedData;
        if (selectedMarketFilter === 'with_market') {
          filteredData = formattedData.filter(item => item.hasMarket === true);
        } else if (selectedMarketFilter === 'without_market') {
          filteredData = formattedData.filter(item => item.hasMarket === false);
        }
        
        setContribuables(filteredData);
        setPagination({
          current_page: 1,
          per_page: filteredData.length,
          total: filteredData.length,
          last_page: 1,
          from: 1,
          to: filteredData.length
        });
        setCurrentPage(1);
        if (resetSelection) setSelectedContacts([]);
      } else {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('per_page', perPage);
        if (filterText) params.append('search', filterText);
        if (selectedType !== 'all') params.append('type', selectedType);
        if (selectedStatus !== 'all') params.append('status', selectedStatus);
        // 👈 AJOUTER le filtre marché pour l'API
        if (selectedMarketFilter === 'with_market') {
          params.append('has_market', 'true');
        } else if (selectedMarketFilter === 'without_market') {
          params.append('has_market', 'false');
        }
        params.append('sort_field', sortField);
        params.append('sort_direction', sortDirection);
        
        const response = await axios.get(
          `${TAXPAYERS_ENDPOINT}/liste?${params.toString()}`, 
          { headers: getHeaders() }
        );
        
        if (response.data && response.data.success) {
          const formattedData = formatApiData(response.data.data);
          setContribuables(formattedData);
          setPagination(response.data.pagination);
          setCurrentPage(response.data.pagination.current_page);
          if (resetSelection) setSelectedContacts([]);
        } else {
          throw new Error('Format de données invalide');
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
      
      if (error.response?.status === 403) {
        setAccessDenied(true);
        setError("Accès non autorisé. Vous n'avez pas les droits pour voir les contribuables.");
        showAlert("Accès non autorisé. Vérifiez vos permissions.", 'danger');
        setContribuables([]);
      } else {
        setError(error.message);
        showAlert(`Erreur: ${error.message}`, 'danger');
        setContribuables([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filterText, selectedType, selectedStatus, selectedMarketFilter, sortField, sortDirection, itemsPerPage]);

    const fetchTrashedContribuables = useCallback(async () => {
    try {
      const response = await axios.get(`${TAXPAYERS_ENDPOINT}/trashed`, { headers: getHeaders() });
      if (response.data && response.data.success) {
        const formattedData = formatApiData(response.data.data);
        setTrashedContribuables(formattedData);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        console.warn('Accès non autorisé pour les contribuables supprimés');
        setTrashedContribuables([]);
      } else {
        console.error('Erreur chargement supprimés:', error);
        setTrashedContribuables([]);
      }
    }
  }, []);

  // ✅ 12. Initialisation
  useEffect(() => {
    fetchContribuables(1, itemsPerPage, true);
    fetchTrashedContribuables();
    fetchCategories();
    fetchLocalites();
    loadGlobalMarcheStats();
  }, []);

  // ✅ 13. Recharger quand les filtres changent
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      if (activeTab === 'active') fetchContribuables(1, itemsPerPage, true);
    }, 500);
    setSearchTimeout(timeout);
    return () => clearTimeout(searchTimeout);
  }, [filterText, selectedType, selectedStatus, selectedMarketFilter, sortField, sortDirection, activeTab, itemsPerPage]);

  // ✅ 14. Recharger les stats quand on change d'onglet
  useEffect(() => {
    if (activeTab === 'active') {
      loadGlobalMarcheStats();
    }
  }, [activeTab]);

  // ✅ 15. Changement de page
  const goToPage = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      fetchContribuables(page, itemsPerPage, false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedContacts([]);
    setCurrentPage(1);
    if (tab === 'trashed') {
      fetchTrashedContribuables();
    }
  };

  // ✅ 16. Données pour l'onglet actif
  const currentData = activeTab === 'active' ? contribuables : trashedContribuables;
  
  const filteredItems = useMemo(() => {
    let filtered = [...currentData];
    if (filterText) {
      const searchLower = filterText.toLowerCase();
      filtered = filtered.filter(item => 
        (item.nom && item.nom.toLowerCase().includes(searchLower)) ||
        (item.contribuable_id && item.contribuable_id.toString().includes(searchLower)) ||
        (item.telephone && item.telephone.includes(searchLower))
      );
    }
    if (selectedType !== 'all') filtered = filtered.filter(item => item.type === selectedType);
    if (selectedStatus !== 'all' && activeTab === 'active') filtered = filtered.filter(item => item.statut === selectedStatus);
    // 👈 Appliquer le filtre marché sur les données déjà chargées
    if (selectedMarketFilter === 'with_market') {
      filtered = filtered.filter(item => item.hasMarket === true);
    } else if (selectedMarketFilter === 'without_market') {
      filtered = filtered.filter(item => item.hasMarket === false);
    }
    
    return filtered;
  }, [currentData, filterText, selectedType, selectedStatus, selectedMarketFilter, activeTab]);

  // ✅ 17. Fonctions utilitaires
  const formatDateTime = (dateString) => {
    if (!dateString) return "Non spécifié";
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getTypeBadge = (type) => {
    const config = {
      particulier: { label: "Particulier", color: "primary", icon: <Person size={10} className="me-1" /> },
      entreprise: { label: "Entreprise", color: "success", icon: <Building size={10} className="me-1" /> },
      commercant: { label: "Commerçant", color: "warning", icon: <Briefcase size={10} className="me-1" /> },
      professionnel: { label: "Professionnel", color: "info", icon: <Tag size={10} className="me-1" /> }
    };
    const cfg = config[type] || { label: type, color: "secondary", icon: null };
    return (
      <Badge bg={cfg.color} className="d-flex align-items-center px-2 py-1" style={{ fontSize: '11px', borderRadius: '20px' }}>
        {cfg.icon}{cfg.label}
      </Badge>
    );
  };

  const getStatusBadge = (statut) => {
    const config = {
      actif: { label: "Actif", color: "success", icon: <CheckCircle className="me-1" size={10} /> },
      inactif: { label: "Inactif", color: "secondary", icon: <XCircle className="me-1" size={10} /> },
      supprimé: { label: "Supprimé", color: "danger", icon: <Trash className="me-1" size={10} /> }
    };
    const cfg = config[statut] || { label: statut, color: "secondary", icon: null };
    return (
      <Badge bg={cfg.color} className="d-flex align-items-center px-2 py-1" style={{ fontSize: '11px', borderRadius: '20px' }}>
        {cfg.icon}{cfg.label}
      </Badge>
    );
  };

  // ✅ 18. Gestion des sélections
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedContacts(filteredItems.map(item => item.id));
    else setSelectedContacts([]);
  };
      
  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]);
  };

  const isAllSelected = filteredItems.length > 0 && selectedContacts.length === filteredItems.length;
  const isSomeSelected = selectedContacts.length > 0 && selectedContacts.length < filteredItems.length;

  // ✅ 19. Suppression
  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!contactToDelete) return;
    setDeletingContact(true);
    try {
      await axios.delete(`${TAXPAYERS_ENDPOINT}/${contactToDelete.contribuable_id}`, { headers: getHeaders() });
      await fetchContribuables(currentPage, itemsPerPage, true);
      await fetchTrashedContribuables();
      setShowDeleteModal(false);
      showNotification('Contribuable supprimé avec succès', 'success');
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas supprimer ce contribuable.", 'danger');
      } else {
        showAlert(`Erreur: ${error.message}`, 'danger');
      }
    } finally {
      setDeletingContact(false);
    }
  };

  // ✅ 20. Restauration
  const handleRestoreContact = (contact) => {
    setContactToRestore(contact);
    setShowRestoreModal(true);
  };

  const handleConfirmRestore = async () => {
    if (!contactToRestore) return;
    setRestoringContact(true);
    try {
      await axios.post(`${TAXPAYERS_ENDPOINT}/${contactToRestore.contribuable_id}/restore`, {}, { headers: getHeaders() });
      await fetchContribuables(1, itemsPerPage, true);
      await fetchTrashedContribuables();
      setShowRestoreModal(false);
      showNotification('Contribuable restauré avec succès', 'success');
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas restaurer ce contribuable.", 'danger');
      } else {
        showAlert(`Erreur: ${error.message}`, 'danger');
      }
    } finally {
      setRestoringContact(false);
    }
  };

  // ✅ 21. Suppression multiple
  const handleBulkDelete = () => {
    if (selectedContacts.length === 0) {
      showAlert("Sélectionnez au moins un contribuable", 'warning');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const handleConfirmBulkDelete = async () => {
    setDeletingContacts(true);
    try {
      await Promise.all(selectedContacts.map(async (id) => {
        const contact = filteredItems.find(c => c.id === id);
        if (contact) return axios.delete(`${TAXPAYERS_ENDPOINT}/${contact.contribuable_id}`, { headers: getHeaders() });
        return Promise.resolve();
      }));
      await fetchContribuables(currentPage, itemsPerPage, true);
      await fetchTrashedContribuables();
      setSelectedContacts([]);
      setShowBulkDeleteModal(false);
      showNotification(`${selectedContacts.length} contribuable(s) supprimé(s)`, 'success');
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas effectuer cette action.", 'danger');
      } else {
        showAlert(`Erreur: ${error.message}`, 'danger');
      }
    } finally {
      setDeletingContacts(false);
    }
  };

  // ✅ 22. Restauration multiple
  const handleBulkRestore = () => {
    if (selectedContacts.length === 0) {
      showAlert("Sélectionnez au moins un contribuable", 'warning');
      return;
    }
    setShowBulkRestoreModal(true);
  };

  const handleConfirmBulkRestore = async () => {
    setRestoringContacts(true);
    try {
      await Promise.all(selectedContacts.map(async (id) => {
        const contact = filteredItems.find(c => c.id === id);
        if (contact) return axios.post(`${TAXPAYERS_ENDPOINT}/${contact.contribuable_id}/restore`, {}, { headers: getHeaders() });
        return Promise.resolve();
      }));
      await fetchContribuables(1, itemsPerPage, true);
      await fetchTrashedContribuables();
      setSelectedContacts([]);
      setShowBulkRestoreModal(false);
      showNotification(`${selectedContacts.length} contribuable(s) restauré(s)`, 'success');
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas effectuer cette action.", 'danger');
      } else {
        showAlert(`Erreur: ${error.message}`, 'danger');
      }
    } finally {
      setRestoringContacts(false);
    }
  };

  // ✅ 23. Édition
  const handleEditContact = (contact) => {
    if (!contact) return;
    setSelectedContact(contact);
    setEditContact({
      contribuable_id: contact.contribuable_id || '',
      nom_contribuable: contact.nom || '',
      email: contact.email || '',
      telephone: contact.telephone || '',
      adresse: contact.adresse || '',
      full_name: contact.admin_full_name || '',
      first_name: contact.first_name || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateContact = async () => {
    if (!selectedContact || !editContact.nom_contribuable?.trim()) {
      showAlert("Le nom est obligatoire", 'warning');
      return;
    }
    setUpdatingContact(true);
    try {
      await axios.patch(`${TAXPAYERS_ENDPOINT}/${selectedContact.contribuable_id}`, {
        nom_entite: editContact.nom_contribuable.trim(),
        adresse: editContact.adresse || "",
        telephone: editContact.telephone?.trim() || "",
        email: editContact.email || "",
        admin_email: editContact.email || "",
        admin_full_name: editContact.full_name || "",
        first_name: editContact.first_name || ""
      }, { headers: getHeaders() });
      await fetchContribuables(currentPage, itemsPerPage, false);
      await fetchTrashedContribuables();
      setShowEditModal(false);
      showNotification('Contribuable mis à jour', 'success');
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas modifier ce contribuable.", 'danger');
      } else {
        showAlert(`Erreur: ${error.message}`, 'danger');
      }
    } finally {
      setUpdatingContact(false);
    }
  };

  // ✅ 24. Génération des numéros de page
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, pagination.current_page - Math.floor(maxVisible / 2));
    let end = Math.min(pagination.last_page, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // ✅ 25. Fonction de génération des avis de passage en masse
  const generateBulkAvisPassage = async () => {
    if (selectedContacts.length === 0) {
      showAlert("Veuillez sélectionner au moins un contribuable", 'warning');
      return;
    }

    const selectedData = filteredItems.filter(item => selectedContacts.includes(item.id));

    // Enregistrement terminé avant d'ouvrir l'impression (la boîte d'impression bloque la page)
    const result = await enregistrerImpressionsAvis(selectedData.map(c => c.contribuable_id));
    showNotification(describeImpressionsResult(result), result.failed.length ? 'error' : 'success');

    printAvisPassage(selectedData.map(c => ({
      nom: c.nom,
      contribuableId: c.contribuable_id,
      quartierNom: getQuartierFromLocalite(c.localite_id)?.nom,
      communeNom: getCommuneFromLocalite(c.localite_id)?.nom
    })));
  };

  // ✅ 26. Affichage en cas d'accès non autorisé
  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
          <div className="container-fluid">
            <Card className="border-0 shadow-sm rounded-3 text-center p-5">
              <div className="mb-4">
                <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ width: 80, height: 80 }}>
                  <Lock size={40} className="text-danger" />
                </div>
              </div>
              <h4 className="fw-bold text-danger">Accès non autorisé</h4>
              <p className="text-muted mb-4 max-width-500 mx-auto">
                Vous n'avez pas les droits nécessaires pour accéder à la liste des contribuables.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button variant="primary" onClick={() => navigate('/dashboard')}>
                  <ArrowRepeat className="me-2" size={16} />
                  Retour au tableau de bord
                </Button>
              </div>
            </Card>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  // ✅ 27. Rendu principal
  return (
    <React.Fragment>
      <Header />
      <style>{`
        .custom-table th {
          background: #f8f9fa;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e9ecef;
        }
        .custom-table td {
          vertical-align: middle;
          padding: 0.75rem;
          font-size: 0.85rem;
        }
        .custom-table tbody tr:hover {
          background-color: rgba(13, 110, 253, 0.04);
        }
        .rounded-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 6px !important;
          cursor: pointer;
        }
        .rounded-checkbox:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        .date-cell {
          font-size: 0.75rem;
          font-weight: 600;
          color: #2c3e50;
          background: #f1f3f5;
          display: inline-block;
          padding: 4px 8px;
          border-radius: 20px;
        }
        .avatar-circle {
          width: 29px;
          height: 29px;
          background: #0d6efd;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 12px;
        }
        .contribuable-name {
          font-weight: 600;
          margin-bottom: 2px;
          text-transform: uppercase;
          font-size: 0.9rem;
        }
        .contribuable-id {
          font-size: 0.7rem;
          color: #6c757d;
        }
        .action-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        .action-btn:hover {
          transform: translateY(-2px);
        }
        .nav-tabs .nav-link {
          font-weight: 500;
          border: none;
          color: #6c757d;
          padding: 10px 20px;
          border-radius: 8px 8px 0 0;
        }
        .nav-tabs .nav-link.active {
          color: #0d6efd;
          background: white;
          border-bottom: 3px solid #0d6efd;
        }
        .nav-tabs .nav-link:hover:not(.active) {
          background: #f8f9fa;
          color: #495057;
        }
        .contribuable-name-wrapper {
          position: relative;
          display: inline-block;
        }
        .contribuable-name-wrapper:hover::after {
          content: attr(data-fullname);
          position: absolute;
          background: #333;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          white-space: nowrap;
          z-index: 1000;
          top: 100%;
          left: 0;
          margin-top: 4px;
        }
        .max-width-500 {
          max-width: 500px;
        }
        .market-filter-select {
          min-width: 150px;
        }
      `}</style>
      
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
        <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
          {notifications.map((n) => (
            <Toast key={n.id} onClose={() => setNotifications(prev => prev.filter(x => x.id !== n.id))} delay={5000} autohide>
              <Toast.Header className={`bg-${n.type === 'success' ? 'success' : n.type === 'error' ? 'danger' : 'info'} text-white`}>
                <strong>{n.type === 'success' ? 'Succès' : n.type === 'error' ? 'Erreur' : 'Info'}</strong>
              </Toast.Header>
              <Toast.Body>{n.message}</Toast.Body>
            </Toast>
          ))}
        </ToastContainer>

        {alertMessage.show && (
          <div className="container-fluid mb-3">
            <Alert variant={alertMessage.type} dismissible onClose={hideAlert} className="shadow-sm border-0">
              {alertMessage.message}
            </Alert>
          </div>
        )}

        <div className="container-fluid">
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <ol className="breadcrumb mb-1 bg-transparent p-0">
                    <li className="breadcrumb-item"><Link to="#" className="text-decoration-none">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active text-primary">Contribuables</li>
                  </ol>
                  <h4 className="main-title mb-0 fw-bold">Gestion des Contribuables</h4>
                  <p className="text-muted mb-0 small">Gérez et suivez tous vos contribuables</p>
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  {activeTab === 'active' && selectedContacts.length > 0 && (
                    <>
                      <Button 
                        variant="warning" 
                        size="sm" 
                        className="rounded-pill text-dark"
                        onClick={generateBulkAvisPassage}
                      >
                        <FilePdf className="me-2" size={14} /> Avis de passage ({selectedContacts.length})
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={handleBulkDelete} className="rounded-pill">
                        <Trash className="me-2" size={14} /> Supprimer ({selectedContacts.length})
                      </Button>
                    </>
                  )}
                  {activeTab === 'trashed' && selectedContacts.length > 0 && (
                    <Button variant="outline-success" size="sm" onClick={handleBulkRestore} className="rounded-pill">
                      <ArrowRepeat className="me-2" size={14} /> Restaurer ({selectedContacts.length})
                    </Button>
                  )}
                  
                  <Button 
                    variant="success" 
                    size="sm" 
                    className="rounded-pill ml-1 mr-1"
                    onClick={() => setShowExportModal(true)}
                    disabled={loading}
                  >
                    <FileExcel className="me-1" size={14} /> Exporter par localité
                  </Button>
                  
                  <Link to="/agent/contribuables/new">
                    <Button variant="primary" className="rounded-pill">
                      <PlusCircle className="me-2" size={16} /> Nouveau contribuable
                    </Button>
                  </Link>
                </div>
              </div>
            </Col>
          </Row>

          {/* Onglets */}
          <Row className="mb-3">
            <Col md={12}>
              <Tabs activeKey={activeTab} onSelect={handleTabChange} className="border-0 bg-white rounded-3 shadow-sm">
                <Tab 
                  eventKey="active" 
                  title={
                    <span>
                      <People className="me-2" size={16} />
                      Actifs ({pagination.total})
                    </span>
                  } 
                />
                <Tab 
                  eventKey="trashed" 
                  title={
                    <span>
                      <Archive className="me-2" size={16} />
                      Supprimés ({trashedContribuables.length})
                    </span>
                  } 
                />
              </Tabs>
            </Col>
          </Row>

          {/* Cartes statistiques marchés */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="border-0 shadow-sm rounded-3 h-100">
                <Card.Body className="d-flex align-items-center p-4">
                  {marcheStats.loading ? (
                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: 60, height: 60 }}>
                      <Spinner animation="border" size="sm" className="text-primary" />
                    </div>
                  ) : (
                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: 60, height: 60 }}>
                      <Buildings size={28} className="text-primary" />
                    </div>
                  )}
                  <div>
                    <h6 className="text-muted mb-1 small text-uppercase fw-semibold">Contribuables avec marché</h6>
                    <h3 className="mb-0 fw-bold">{marcheStats.loading ? '...' : marcheStats.withMarket}</h3>
                    <p className="text-muted small mb-0">
                      {marcheStats.total > 0 && !marcheStats.loading 
                        ? ((marcheStats.withMarket / marcheStats.total) * 100).toFixed(1) 
                        : 0}% du total
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm rounded-3 h-100" >
                <Card.Body className="d-flex align-items-center p-4">
                  {marcheStats.loading ? (
                    <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: 60, height: 60 }}>
                      <Spinner animation="border" size="sm" className="text-warning" />
                    </div>
                  ) : (
                    <div className="bg-warning bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-4" style={{ width: 60, height: 60 }}>
                      <XCircle size={28} className="text-warning" />
                    </div>
                  )}
                  <div>
                    <h6 className="text-muted mb-1 small text-uppercase fw-semibold">Contribuables hors marché</h6>
                    <h3 className="mb-0 fw-bold">{marcheStats.loading ? '...' : marcheStats.withoutMarket}</h3>
                    <p className="text-muted small mb-0">
                      {marcheStats.total > 0 && !marcheStats.loading 
                        ? ((marcheStats.withoutMarket / marcheStats.total) * 100).toFixed(1) 
                        : 0}% du total
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="border-0 shadow-sm rounded-3" style={{minHeight:'115px'}}>
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small text-muted">Répartition des marchés</span>
                    <span className="small fw-semibold">
                      {marcheStats.loading ? 'Chargement...' : `${marcheStats.withMarket} avec marché / ${marcheStats.withoutMarket} hors marché`}
                    </span>
                  </div>
                  <div className="d-flex" style={{ height: '8px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#e9ecef' }}>
                    <div 
                      className="bg-primary" 
                      style={{ 
                        width: `${marcheStats.total > 0 && !marcheStats.loading ? (marcheStats.withMarket / marcheStats.total) * 100 : 0}%`,
                        transition: 'width 0.5s ease'
                      }}
                    />
                    <div 
                      className="bg-warning" 
                      style={{ 
                        width: `${marcheStats.total > 0 && !marcheStats.loading ? (marcheStats.withoutMarket / marcheStats.total) * 100 : 0}%`,
                        transition: 'width 0.5s ease'
                      }}
                    />
                  </div>
                  <div className="d-flex justify-content-between mt-2">
                    <div className="d-flex align-items-center">
                      <span className="badge bg-primary me-2" style={{ width: 12, height: 12, padding: 0 }} />
                      <span className="small text-muted">Avec marché</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-warning me-2" style={{ width: 12, height: 12, padding: 0 }} />
                                            <span className="small text-muted">Hors marché</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Filtres */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3 align-items-center">
                    <Col md={4}>
                      <InputGroup className="rounded-3 overflow-hidden">
                        <InputGroup.Text className="bg-white border-end-0">
                          <Search className="text-primary" size={18} />
                        </InputGroup.Text>
                        <Form.Control 
                          placeholder="Rechercher un contribuable..." 
                          value={filterText} 
                          onChange={(e) => setFilterText(e.target.value)}
                          className="border-start-0"
                        />
                      </InputGroup>
                    </Col>
                    
                    <Col md={2}>
                      <Form.Select 
                        value={selectedMarketFilter} 
                        onChange={(e) => setSelectedMarketFilter(e.target.value)}
                        className="rounded-3 market-filter-select"
                      >
                        {marketFilters.map(filter => (
                          <option key={filter.value} value={filter.value}>
                            {filter.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    
                    <Col md={2}>
                      <Form.Select 
                        value={itemsPerPage} 
                        onChange={(e) => { 
                          const value = Number(e.target.value);
                          setItemsPerPage(value); 
                          fetchContribuables(1, value, true);
                        }}
                        className="rounded-3"
                      >
                        <option value={5}>5 par page</option>
                        <option value={10}>10 par page</option>
                        <option value={25}>25 par page</option>
                        <option value={50}>50 par page</option>
                        <option value={100}>100 par page</option>
                        <option value={-1}>Tous</option>
                      </Form.Select>
                    </Col>
                   
                    <Col md={2}>
                      <Button variant="outline-secondary" onClick={() => { 
                        setFilterText(""); 
                        setSelectedType("all"); 
                        setSelectedStatus("all");
                        setSelectedMarketFilter("all");
                      }} className="rounded-3 w-100">
                        <X className="me-1" size={14} /> Réinitialiser
                      </Button>
                    </Col>
                    <Col md={2}>
                      <Button variant="primary" onClick={() => fetchContribuables(1, itemsPerPage, true)} className="rounded-3 w-100">
                        <ArrowRepeat className="me-1" size={14} /> Actualiser
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau principal */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
                <Card.Header className="bg-white border-0 py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <Building className="text-primary me-2" size={20} />
                      <span className="fw-semibold">Liste des Contribuables</span>
                      <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                        {activeTab === 'active' ? pagination.total : trashedContribuables.length} au total
                      </Badge>
                    </div>
                    {selectedContacts.length > 0 && (
                      <Badge bg="primary" className="rounded-pill">
                        {selectedContacts.length} sélectionné(s)
                      </Badge>
                    )}
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="custom-table mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input 
                              type="checkbox" 
                              className="form-check-input rounded-checkbox" 
                              checked={isAllSelected && filteredItems.length > 0} 
                              ref={input => { if (input) input.indeterminate = isSomeSelected; }} 
                              onChange={handleSelectAll} 
                            />
                          </th>
                          <th>Contribuable</th>
                          <th>Marché</th>
                          <th>Téléphone</th>
                          <th>Email</th>
                          <th>Statut</th>
                          <th>Créé le</th>
                          <th style={{ width: '130px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <Spinner animation="border" variant="primary" />
                              <p className="mt-2 text-muted small">Chargement des données...</p>
                            </td>
                          </tr>
                        ) : filteredItems.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <Person size={48} className="text-muted mb-3" />
                              <p className="text-muted">Aucun contribuable trouvé</p>
                              {selectedMarketFilter !== 'all' && (
                                <p className="text-muted small">
                                  Essayez de modifier le filtre de marché
                                </p>
                              )}
                            </td>
                          </tr>
                        ) : (
                          filteredItems.map((c) => (
                            <tr key={c.id}>
                              <td>
                                <input 
                                  type="checkbox" 
                                  className="form-check-input rounded-checkbox" 
                                  checked={selectedContacts.includes(c.id)} 
                                  onChange={() => handleSelectContact(c.id)} 
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="avatar-circle me-3">
                                    {c.nom?.charAt(0) || 'C'} 
                                  </div>
                                  <div>
                                    <div className="contribuable-name-wrapper" data-fullname={c.nom}>
                                      <div className="contribuable-name">
                                        {c.nom}
                                      </div>
                                    </div>
                                    <div className="contribuable-id">ID: {c.contribuable_id}</div>
                                  </div>
                                </div>
                              </td>
                              <td>{getMarcheBadge(c.hasMarket, c.marcheNom)}</td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Phone size={12} className="text-muted me-1" />
                                  <span className="small">{c.telephone || '-'}</span>
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <Envelope size={12} className="text-muted me-1" />
                                  <span className="small text-truncate" style={{ maxWidth: '180px' }}>{c.email || '-'}</span>
                                </div>
                              </td>
                              <td>{getStatusBadge(c.statut)}</td>
                              <td>
                                <div className="date-cell">
                                  <Calendar3 size={10} className="me-1" />
                                  {formatDateTime(c.created_at)}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  {activeTab === 'active' ? (
                                    <>
                                      <Button 
                                        as={Link} 
                                        to={`/agent/ContribuableDetails/${c.contribuable_id}`} 
                                        size="sm" 
                                        variant="outline-primary" 
                                        title="Détails" 
                                        className="action-btn"
                                      >
                                        <Eye size={14} />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline-info" 
                                        title="Modifier" 
                                        onClick={() => handleEditContact(c)} 
                                        className="action-btn"
                                      >
                                        <Pencil size={14} />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline-danger" 
                                        title="Supprimer" 
                                        onClick={() => handleDeleteContact(c)} 
                                        className="action-btn"
                                      >
                                        <Trash size={14} />
                                      </Button>
                                    </>
                                  ) : (
                                    <Button 
                                      size="sm" 
                                      variant="outline-success" 
                                      title="Restaurer" 
                                      onClick={() => handleRestoreContact(c)} 
                                      className="action-btn"
                                    >
                                      <ArrowRepeat size={14} />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {activeTab === 'active' && itemsPerPage !== -1 && pagination.last_page > 1 && (
                    <div className="p-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-3 bg-light">
                      <div className="text-muted small">
                        Affichage de <strong>{pagination.from}</strong> à <strong>{pagination.to}</strong> sur <strong>{pagination.total}</strong> contribuables
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.First disabled={pagination.current_page === 1} onClick={() => goToPage(1)} className="rounded" />
                        <Pagination.Prev disabled={pagination.current_page === 1} onClick={() => goToPage(pagination.current_page - 1)} className="rounded" />
                        {getPageNumbers().map(pageNum => (
                          <Pagination.Item 
                            key={pageNum} 
                            active={pageNum === pagination.current_page} 
                            onClick={() => goToPage(pageNum)}
                            className="rounded"
                          >
                            {pageNum}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={pagination.current_page === pagination.last_page} onClick={() => goToPage(pagination.current_page + 1)} className="rounded" />
                        <Pagination.Last disabled={pagination.current_page === pagination.last_page} onClick={() => goToPage(pagination.last_page)} className="rounded" />
                      </Pagination>
                    </div>
                  )}
                  
                  {/* Message "Tous" */}
                  {activeTab === 'active' && itemsPerPage === -1 && (
                    <div className="p-3 border-top bg-light">
                      <div className="text-muted small text-center">
                        Affichage de tous les <strong>{pagination.total}</strong> contribuables
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Modal Export */}
        <Modal 
          show={showExportModal} 
          onHide={() => {
            setShowExportModal(false);
            setSelectedCommune('');
            setSelectedQuartier('');
          }} 
          size="lg" 
          centered
        >
          <Modal.Header closeButton className="bg-success text-white border-0">
            <Modal.Title>
              <FileExcel className="me-2" size={20} /> Exporter les contribuables par localité
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="alert alert-info mb-4">
              <GeoAlt className="me-2" size={18} />
              Sélectionnez une commune ou un quartier pour exporter uniquement les contribuables de cette localité.
              <br />
              <small className="text-muted">Laissez vide pour exporter tous les contribuables.</small>
            </div>
            
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <Building size={16} className="me-1" /> Commune
                  </Form.Label>
                  
                  <Select
                    options={communes.map(commune => ({
                      value: commune.localite_id,
                      label: commune.nom
                    }))}
                    value={communes
                      .map(commune => ({
                        value: commune.localite_id,
                        label: commune.nom
                      }))
                      .find(opt => opt.value === selectedCommune) || null
                    }
                    onChange={(option) => {
                      setSelectedCommune(option ? option.value : '');
                      setSelectedQuartier('');
                    }}
                    isClearable
                    isSearchable
                    placeholder={loadingLocalites ? "Chargement des communes..." : "Rechercher une commune..."}
                    isLoading={loadingLocalites}
                    noOptionsMessage={() => "Aucune commune trouvée"}
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    <GeoAlt size={16} className="me-1" /> Quartier
                  </Form.Label>
                  
                  <Select
                    options={quartiers
                      .filter(q => !selectedCommune || String(q.parent_id) === String(selectedCommune))
                      .map(quartier => {
                        const communeName = communes.find(c => String(c.localite_id) === String(quartier.parent_id))?.nom || '';
                        return {
                          value: quartier.localite_id,
                          label: `${quartier.nom}${communeName ? ` (${communeName})` : ''}`
                        };
                      })
                    }
                    value={quartiers
                      .filter(q => !selectedCommune || String(q.parent_id) === String(selectedCommune))
                      .map(quartier => ({
                        value: quartier.localite_id,
                        label: `${quartier.nom}${communes.find(c => String(c.localite_id) === String(quartier.parent_id))?.nom ? ` (${communes.find(c => String(c.localite_id) === String(quartier.parent_id))?.nom})` : ''}`
                      }))
                      .find(opt => opt.value === selectedQuartier) || null
                    }
                    onChange={(option) => setSelectedQuartier(option ? option.value : '')}
                    isClearable
                    isSearchable
                    placeholder={
                      loadingLocalites 
                        ? "Chargement des quartiers..." 
                        : !selectedCommune 
                          ? "Sélectionnez d'abord une commune" 
                          : "Rechercher un quartier..."
                    }
                    isLoading={loadingLocalites}
                    isDisabled={!selectedCommune && !loadingLocalites}
                    noOptionsMessage={() => 
                      !selectedCommune 
                        ? "Sélectionnez d'abord une commune" 
                        : "Aucun quartier trouvé pour cette commune"
                    }
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                  
                  {selectedCommune && quartiers.filter(q => String(q.parent_id) === String(selectedCommune)).length === 0 && (
                    <Form.Text className="text-warning d-block mt-1">
                      ⚠️ Aucun quartier trouvé pour cette commune
                    </Form.Text>
                  )}
                  
                  {selectedQuartier && (
                    <Form.Text className="text-success d-block mt-1">
                      ✅ Quartier sélectionné : {
                        quartiers.find(q => String(q.localite_id) === String(selectedQuartier))?.nom || ''
                      }
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            <div className="mt-4 p-3 bg-light rounded-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <strong>Résumé de l'export :</strong>
                </div>
                <div className="d-flex gap-3 flex-wrap">
                  {selectedCommune && (
                    <Badge bg="primary" className="p-2">
                      Commune: {communes.find(c => String(c.localite_id) === String(selectedCommune))?.nom || 'Sélectionnée'}
                    </Badge>
                  )}
                  {selectedQuartier && (
                    <Badge bg="info" className="p-2">
                      Quartier: {quartiers.find(q => String(q.localite_id) === String(selectedQuartier))?.nom || 'Sélectionné'}
                    </Badge>
                  )}
                  {!selectedCommune && !selectedQuartier && (
                    <Badge bg="success" className="p-2">
                      ✅ Tous les contribuables
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light">
            <Button 
              variant="light" 
              onClick={() => {
                setShowExportModal(false);
                setSelectedCommune('');
                setSelectedQuartier('');
              }} 
              className="rounded-3"
            >
              Annuler
            </Button>
            <Button 
              variant="success" 
              onClick={exportContribuablesByLocalite} 
              disabled={exportLoading}
              className="rounded-3"
            >
              {exportLoading ? (
                <><Spinner size="sm" className="me-2" /> Export en cours...</>
              ) : (
                <><FileExcel className="me-2" size={16} /> Exporter vers Excel</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modals existants */}
        <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
          <Modal.Header closeButton className="bg-primary text-white border-0">
            <Modal.Title><Pencil className="me-2" size={18} /> Modifier le contribuable</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {selectedContact && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Nom <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    value={editContact.nom_contribuable || ''} 
                    onChange={(e) => setEditContact({...editContact, nom_contribuable: e.target.value.toLocaleUpperCase('fr-FR')})}
                    className="rounded-3"
                  />
                </Form.Group>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Nom complet (admin)</Form.Label>
                      <Form.Control type="text" value={editContact.full_name || ''} onChange={(e) => setEditContact({...editContact, full_name: e.target.value})} className="rounded-3" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Prénom (admin)</Form.Label>
                      <Form.Control type="text" value={editContact.first_name || ''} onChange={(e) => setEditContact({...editContact, first_name: e.target.value})} className="rounded-3" />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Téléphone <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="tel" value={editContact.telephone || ''} onChange={(e) => setEditContact({...editContact, telephone: e.target.value})} className="rounded-3" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold">Email</Form.Label>
                      <Form.Control type="email" value={editContact.email || ''} onChange={(e) => setEditContact({...editContact, email: e.target.value})} className="rounded-3" />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Adresse</Form.Label>
                  <Form.Control type="text" value={editContact.adresse || ''} onChange={(e) => setEditContact({...editContact, adresse: e.target.value})} className="rounded-3" />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light">
            <Button variant="light" onClick={() => setShowEditModal(false)} className="rounded-3">Annuler</Button>
            <Button variant="primary" onClick={handleUpdateContact} disabled={updatingContact} className="rounded-3">
              {updatingContact ? <Spinner size="sm" className="me-2" /> : <Check className="me-2" size={16} />} 
              Mettre à jour
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title><ExclamationCircle className="text-warning me-2" size={20} /> Confirmer la suppression</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center p-4">
            <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: 70, height: 70 }}>
              <Trash size={30} className="text-danger" />
            </div>
            <h5>Supprimer ce contribuable ?</h5>
            {contactToDelete && (
              <div className="mt-3 p-3 bg-light rounded-3">
                <strong>{contactToDelete.nom}</strong><br />
                <small className="text-muted">ID: {contactToDelete.contribuable_id}</small>
              </div>
            )}
            <p className="text-muted mt-3 small">Action réversible - vous pourrez le restaurer depuis l'onglet "Supprimés".</p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center">
            <Button variant="light" onClick={() => setShowDeleteModal(false)} className="rounded-3 px-4">Annuler</Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={deletingContact} className="rounded-3 px-4">
              {deletingContact ? <Spinner size="sm" className="me-2" /> : <Trash className="me-2" size={16} />} 
              Supprimer
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title><ArrowRepeat className="text-success me-2" size={20} /> Restaurer le contribuable</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center p-4">
            <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: 70, height: 70 }}>
              <ArrowRepeat size={30} className="text-success" />
            </div>
            <h5>Restaurer ce contribuable ?</h5>
            {contactToRestore && (
              <div className="mt-3 p-3 bg-light rounded-3">
                <strong>{contactToRestore.nom}</strong><br />
                <small className="text-muted">ID: {contactToRestore.contribuable_id}</small>
              </div>
            )}
            <p className="text-muted mt-3 small">Le contribuable sera réactivé et apparaîtra dans la liste des actifs.</p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center">
            <Button variant="light" onClick={() => setShowRestoreModal(false)} className="rounded-3 px-4">Annuler</Button>
            <Button variant="success" onClick={handleConfirmRestore} disabled={restoringContact} className="rounded-3 px-4">
              {restoringContact ? <Spinner size="sm" className="me-2" /> : <ArrowRepeat className="me-2" size={16} />} 
              Restaurer
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title><ExclamationCircle className="text-warning me-2" size={20} /> Suppression multiple</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center p-4">
            <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: 70, height: 70 }}>
              <Trash size={30} className="text-danger" />
            </div>
            <h5>Supprimer {selectedContacts.length} contribuable(s) ?</h5>
            <p className="text-muted mt-3 small">Action réversible - vous pourrez les restaurer depuis l'onglet "Supprimés".</p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center">
            <Button variant="light" onClick={() => setShowBulkDeleteModal(false)} className="rounded-3 px-4">Annuler</Button>
            <Button variant="danger" onClick={handleConfirmBulkDelete} disabled={deletingContacts} className="rounded-3 px-4">
              {deletingContacts ? <Spinner size="sm" className="me-2" /> : <Trash className="me-2" size={16} />} 
              Supprimer
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showBulkRestoreModal} onHide={() => setShowBulkRestoreModal(false)} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title><ArrowRepeat className="text-success me-2" size={20} /> Restauration multiple</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center p-4">
            <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: 70, height: 70 }}>
              <ArrowRepeat size={30} className="text-success" />
            </div>
            <h5>Restaurer {selectedContacts.length} contribuable(s) ?</h5>
            <p className="text-muted mt-3 small">Ces contribuables seront réactivés.</p>
          </Modal.Body>
          <Modal.Footer className="border-0 justify-content-center">
            <Button variant="light" onClick={() => setShowBulkRestoreModal(false)} className="rounded-3 px-4">Annuler</Button>
            <Button variant="success" onClick={handleConfirmBulkRestore} disabled={restoringContacts} className="rounded-3 px-4">
              {restoringContacts ? <Spinner size="sm" className="me-2" /> : <ArrowRepeat className="me-2" size={16} />} 
              Restaurer
            </Button>
          </Modal.Footer>
        </Modal>

        <Footer />
      </div>
    </React.Fragment>
  );
}